import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultResponse } from '../session';
import { environment } from '../../../../environments/environment';
import { map, Observable, tap } from 'rxjs';
import { DataSourceStore } from './data-source.store';
import { QueryParams } from '../../../../../../api/src/data-source/models';
import { BaseEntityService } from '../base-entity.service';
import { DataSource, PublicDataSource } from '../../../../../../api/src/data-source/data-source.entity';
import { DataSourceUpdateDto } from '../../../../../../api/src/data-source/dtos/data-source.update.dto';
import { catchError } from "rxjs/operators";
import { DataSourceStoreWriteDto } from '../../../../../../api/src/data-source/dtos/data-source-store-write.dto';
import { DataSourceStoreDeleteDto } from '../../../../../../api/src/data-source/dtos/data-source-store-delete.dto';
import { FileDataImportUploadObject } from '../dataImport/dataImport.service';
import { GlobalService } from '../global'

@Injectable({ providedIn: 'root' })
export class DataSourceService extends BaseEntityService {
	constructor(
		private http: HttpClient,
		private dataSourceStore: DataSourceStore,
		private readonly globalService: GlobalService
	) {
		super();
	}

	find(queryParams?: any) {
		const params = {
			page: queryParams?.page || 1,
			perPage: queryParams?.limit,
			sortBy: queryParams?.sortBy?.id || 'slug',
			order: queryParams?.sortBy?.direction || 'ASC',
			includeCollections: true
		}
		if (queryParams?.query) {
			params['query'] = queryParams.query;
		}

		return this.http.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/find`,{}, { params })
			.pipe(
				tap(response => {
					this.dataSourceStore.set(response.data?.results);
				})
			);
	}

	findByIds(ids: string[]) {
		// Validate and sanitize IDs
		if (!ids || !Array.isArray(ids) || ids.length === 0) {
			return this.http.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/find`, {}, { params: { includeCollections: 'true' } }).pipe(
				tap(response => {
					this.dataSourceStore.set(response.data?.results || []);
				})
			);
		}

		// Filter out invalid IDs
		const validIds = ids.filter(id => typeof id === 'string' && id.length > 0);

		return this.http.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/find`, { 
			ids: validIds,
		}).pipe(
			tap(response => {
				this.dataSourceStore.set(response.data?.results || []);
			}),
			catchError(err => {
				console.error('Error loading data sources by IDs:', err);
				this.dataSourceStore.set([]);
				throw err;
			})
		);
	}

	query(slug: string, params: QueryParams) {
		return this.http.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/${slug}/query`, {
			params
		});
	}

	/**
	 * Adapter method for CollectionContainer: expects signature (queryParams, _setStore?, _setActive?, slug)
	 * Maps collection QueryParams to backend QueryParams and calls the real query(slug, params).
	 */
	queryCollection(queryParams: any, _setStore?: boolean, _setActive?: boolean, slug?: string) {
		if (!slug) {
			throw new Error('DataSourceService.queryCollection requires a slug as the 4th argument');
		}
		const serverParams: QueryParams = {
			search: queryParams?.search || queryParams?.query,
			filter: queryParams?.filter,
			sort: queryParams?.sortBy?.id,
			sortStrategy: queryParams?.sortBy?.direction,
			page: queryParams?.page,
			pageSize: queryParams?.limit,
			mediaType: queryParams?.mediaType,
			doNotMap: queryParams?.doNotMap,
			cursor: queryParams?.cursor
		};

		return this.query(slug, serverParams);
	}

	queryBySiteId(slug: string, params: QueryParams) {
		return this.http.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/${slug}/query/${environment.organizationId}`, {
			params
		});
	}

	get({ page = 1, limit = 10, sort, filter = {} }): Observable<DefaultResponse<PublicDataSource>> {
		return this.http
			.post<DefaultResponse<any>>(
				`${environment.apiUrl}/data-source/find`,
				filter,
				{ params: { page, perPage: limit, sortBy: sort.sortBy, order: sort.direction } }
			)
			.pipe(
				map(response => ({
					totalResults: response.data.totalResults,
					data: response.data?.results,
					status: response.status,
					message: response.message,
					numPages: response.data.numPages,
					perPage: response.data.perPage,
					page: response.data.page
				})),
				tap(response => {
					this.dataSourceStore.set(response.data);
				})
			);
	}

	getWithSearch(queryParams: any): Observable<DefaultResponse<PublicDataSource>> {
		console.log('🔍 DataSourceService.getWithSearch called with:', queryParams);

		// Extract pagination and sort parameters
		const page = queryParams?.page || 1;
		const limit = queryParams?.limit || 10;
		const sort = queryParams?.sort || { sortBy: 'slug', direction: 'ASC' };
		const filter = queryParams?.filter || {};
		const search = queryParams?.search;

		// Include search in the filter if provided
		const requestFilter = search ? { ...filter, search } : filter;

		console.log('🔍 Making request with:', { page, limit, sort, requestFilter });

		return this.http
			.post<DefaultResponse<any>>(
				`${environment.apiUrl}/data-source/find`,
				requestFilter,
				{ params: { page: page, perPage: limit, sortBy: sort.sortBy, order: sort.direction } }
			)
			.pipe(
				map(response => ({
					totalResults: response.data.totalResults,
					data: response.data?.results,
					status: response.status,
					message: response.message,
					numPages: response.data.numPages,
					perPage: response.data.perPage,
					page: response.data.page
				})),
				tap(response => {
					console.log('🔍 DataSourceService response:', response);
					this.dataSourceStore.set(response.data);
				})
			);
	}

	getBySlug(slug: string, setStore = true, setActive = true): Observable<PublicDataSource> {
		this.dataSourceStore.setActive(null);
		return this.http.get<DefaultResponse<PublicDataSource>>(`${environment.apiUrl}/data-source/${slug}`).pipe(
			map(response => response.data),
			tap(dataSource => {
				if (setStore){
					this.dataSourceStore.upsert(dataSource.id, dataSource);
				}

				if (setActive){
					this.dataSourceStore.setActive(dataSource.id);
				}
			})
		);
	}

	public create(dataSourceAddDto: Partial<DataSource>): Observable<DefaultResponse<PublicDataSource>> {
		return this.http.post<DefaultResponse<PublicDataSource>>(`${environment.apiUrl}/data-source`, dataSourceAddDto).pipe(
			tap(res => {
				if (res.status === 'succeeded') {
					this.dataSourceStore.upsert(res.data.id, res.data);
				}
			})
		);
	}
	public update(dataSourceSlug: string, dataSourceUpdateDto: DataSourceUpdateDto): Observable<DefaultResponse<PublicDataSource>> {
		return this.http
			.put<DefaultResponse<PublicDataSource>>(`${environment.apiUrl}/data-source/${dataSourceSlug}`, dataSourceUpdateDto)
			.pipe(
				tap(res => {
					if (res.status === 'succeeded') {
						this.dataSourceStore.upsert(res.data.id, res.data);
					}
				})
			);
	}

	public delete(dataSourceSlug: string): Observable<DefaultResponse<PublicDataSource>> {
		return this.http.delete<DefaultResponse<PublicDataSource>>(`${environment.apiUrl}/data-source/${dataSourceSlug}`).pipe(
			tap(res => {
				if (res.status === 'succeeded') {
					this.dataSourceStore.remove(dataSourceSlug);
				}
			}),
			catchError(err => {
				// Handle 403 Forbidden errors specifically
				if (err.status === 403) {
					const errorMessage = err.error?.message || err.message || 'You do not have permission to delete this data source.';
					this.globalService.triggerErrorMessage(undefined, errorMessage);
				} else {
					// Handle other errors
					const errorMessage = err.error?.message || err.message || 'There was an error deleting the data source.';
					this.globalService.triggerErrorMessage(undefined, errorMessage);
				}
				throw err;
			})
		);
	}

	public getFromSource(reqBody: { targetId: string, accessKey: string, slug: string, type: string }): Observable<any> {
		return this.http.post<DefaultResponse<any>>(`${environment.apiUrl}/data-source/get-from-source`, reqBody).pipe(
			catchError(err => {
				return err;
			})
		);
	}

	public writeData(dataSourceSlug: string, dataSourceStoreWriteDto: DataSourceStoreWriteDto): Observable<DefaultResponse<PublicDataSource>> {
		return this.http.post<DefaultResponse<PublicDataSource>>(`${environment.apiUrl}/data-source/${dataSourceSlug}/store`, dataSourceStoreWriteDto).pipe(
			tap(res => {
				if (res.status === 'succeeded') {
				}
			})
		);
	}

	public importCsvData(dataSourceSlug: string, fileReq: FileDataImportUploadObject): Observable<DefaultResponse<PublicDataSource>> {
		const formData: FormData = new FormData();
		formData.append('file', fileReq.file);

		return this.http.post<DefaultResponse<PublicDataSource>>(`${environment.apiUrl}/data-source/${dataSourceSlug}/import/csv`, formData).pipe(
			catchError(err => {
				this.globalService.triggerErrorMessage(undefined, $localize`:@@fileImportError:There was an issue importing your file.`);
				throw err;
			}),
			tap(res => {
				if (res.status === 'succeeded') {
				}
			})
		);
	}

	public getCsvDataTemplate(dataSourceSlug: string): Observable<any> {
		return this.http.get(`${environment.apiUrl}/data-source/${dataSourceSlug}/import/template/csv`, {responseType: 'blob'});
	}

	public deleteData(dataSourceSlug: string, dataSourceStoreDeleteDto: DataSourceStoreDeleteDto): Observable<DefaultResponse<undefined>> {
		return this.http.delete<DefaultResponse<undefined>>(`${environment.apiUrl}/data-source/${dataSourceSlug}/store`, {body: dataSourceStoreDeleteDto}).pipe(
			tap(res => {
				if (res.status === 'succeeded') {
				}
			})
		);
	}

	bulkGetFromUrl(id: string = '') {
		return this.http.get(`${environment.apiUrl}/data-source/bulk-export/${id}`, {
				responseType: 'json'
			})
			.pipe(
				catchError(err => {
					this.globalService.triggerErrorMessage(undefined, $localize `:Error Message@@reportExportJSONExportErrorMessageText:Error exporting JSON`);
					throw err;
				})
			);
	}
}
